import { RelationsGet, RelationsListReq, Ulid } from "@kase/my-marathon-communication";
import { ReadonlySignal, computed } from "@preact/signals";
import { ApiSliceParams } from "../interfaces";
import { createListController } from "../utils";
import { CreateRelationRequest, Relation, RelationsKind } from "./interfaces";

const getSourceKey = (kind: RelationsKind, id: Readonly<Ulid>) =>
    `source:${kind}:${id.toString()}`;

export const makeRelation = ({ connection }: ApiSliceParams) => {
    const bySourceCache: Record<string, ReadonlySignal<Relation[]>> = {};

    const { items, listen } = createListController<
        RelationsGet,
        Relation,
        RelationsListReq
    >({
        connection,
        list: (con, filter) => con.relations.items(filter),
        getKey: (filter) =>
            filter.$case === "source"
                ? getSourceKey(
                      filter.source.source_kind,
                      filter.source.source_id as Readonly<Ulid>,
                  )
                : `target:${filter.target.target_kind}:${(
                      filter.target.target_id as Readonly<Ulid>
                  ).toString()}`,
        name: "relation",
    });

    const bySource = (kind: RelationsKind, id: Readonly<Ulid>) => {
        const key = getSourceKey(kind, id);

        if (!bySourceCache[key]) {
            bySourceCache[key] = computed(() =>
                items.value.filter(
                    (item) =>
                        item.source_kind === kind &&
                        item.source_id.equals(id),
                ),
            );

            listen({
                $case: "source",
                source: { source_kind: kind, source_id: id },
            });
        }

        return bySourceCache[key];
    };

    const create = async (request: CreateRelationRequest) => {
        const con = connection.value;

        return con.relations.create({
            source_id: request.sourceId,
            source_kind: request.sourceKind,
            target_id: request.targetId,
            target_kind: request.targetKind,
        });
    };

    const remove = async (id: Readonly<Ulid>) => {
        const con = connection.value;

        return con.relations.delete(id);
    };

    return {
        bySource,
        create,
        remove,
    };
};
